import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Autorenew as RestartIcon,
  AutoAwesome as PromptIcon,
  DeleteOutline as DeleteIcon,
  DynamicFeed as FeedIcon,
  LockOutlined as LockIcon,
  PlayCircleOutline as PlayIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { PageLayout } from '../layout/primitives';
import type {
  ScrollBrowserStreamSession,
  ScrollFeedItem,
  ScrollScorerRefresh,
  ScrollScorerStatus,
  ScrollScorerState,
  ScrollSessionStatus,
  ScrollSourceId,
  ScrollVideoBrowserSession,
} from '../store/scroll';
import { useScrollStore } from '../store/scroll';

const STATUS_LABELS: Record<ScrollSessionStatus, string> = {
  connected: 'Connected',
  disabled: 'Disabled',
  not_connected: 'Needs session',
  stale: 'Saved / inactive',
};

const sourceOrder: ScrollSourceId[] = ['x', 'instagram'];
const SOURCE_HOME_URLS: Record<ScrollSourceId, string> = {
  x: 'https://x.com/home',
  instagram: 'https://www.instagram.com/',
};

const getVideoPosterUrl = (item: ScrollFeedItem): string | null =>
  item.video?.poster_url ?? item.preview_image_url ?? null;

interface SessionDraft {
  loginHint: string;
  userAgent: string;
  storageState: string;
  browserUrl: string;
  browserText: string;
  error: string | null;
}

const EMPTY_SESSION_DRAFT: SessionDraft = {
  loginHint: '',
  userAgent: '',
  storageState: '',
  browserUrl: '',
  browserText: '',
  error: null,
};

const formatPublishedAt = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatBytes = (value: number): string => {
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`;
  }
  return `${value} B`;
};

const formatScorerSummary = (rankingStatus: ScrollScorerStatus | null): string | null => {
  if (!rankingStatus?.last_refresh_at) {
    return null;
  }
  const attempted = rankingStatus.last_attempted_item_count;
  const ranked = rankingStatus.last_ranked_item_count;
  const fallback = rankingStatus.last_fallback_item_count;
  if (attempted === 0) {
    return `Latest refresh ${formatPublishedAt(rankingStatus.last_refresh_at)} did not yield any items.`;
  }
  if (rankingStatus.latest_refresh_used_fallback) {
    return `Latest refresh ${formatPublishedAt(rankingStatus.last_refresh_at)} considered ${attempted} item${attempted === 1 ? '' : 's'} and left ${fallback} in fallback order.`;
  }
  return `Latest refresh ${formatPublishedAt(rankingStatus.last_refresh_at)} cloud-ranked ${ranked} item${ranked === 1 ? '' : 's'}.`;
};

const SCORER_MODE_UI: Record<
  ScrollScorerState,
  {
    chipColor: 'success' | 'warning' | 'default';
    label: string;
  }
> = {
  configured: { chipColor: 'success', label: 'Cloud ranking ready' },
  degraded: { chipColor: 'warning', label: 'Fallback ordering active' },
  unconfigured: { chipColor: 'default', label: 'Fallback ordering only' },
};

const getRankingAlertConfig = (
  rankingStatus: ScrollScorerStatus | null,
): { severity: 'info' | 'warning'; message: string } | null => {
  if (!rankingStatus || rankingStatus.state === 'configured') {
    return null;
  }
  if (rankingStatus.state === 'degraded') {
    return {
      severity: 'warning',
      message: rankingStatus.last_error
        ? `${rankingStatus.detail} Last error: ${rankingStatus.last_error}`
        : rankingStatus.detail,
    };
  }
  return {
    severity: 'info',
    message: rankingStatus.detail,
  };
};

const formatRefreshCounts = (
  attempted: number,
  ranked: number,
  fallback: number,
): string => {
  if (attempted === 0) {
    return '0 candidates';
  }
  const parts = [`${attempted} candidate${attempted === 1 ? '' : 's'}`];
  if (ranked > 0) {
    parts.push(`${ranked} cloud-ranked`);
  }
  if (fallback > 0) {
    parts.push(`${fallback} fallback`);
  }
  return parts.join(' • ');
};

const ScorerHistoryEntry: React.FC<{
  entry: ScrollScorerRefresh;
}> = ({ entry }) => {
  const mode = SCORER_MODE_UI[entry.state];
  const refreshedAt = formatPublishedAt(entry.refreshed_at) ?? entry.refreshed_at;
  const rankedAt = formatPublishedAt(entry.ranked_at);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack spacing={1}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {refreshedAt}
          </Typography>
          <Chip size="small" color={mode.chipColor} label={mode.label} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {entry.detail}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            label={formatRefreshCounts(
              entry.attempted_item_count,
              entry.ranked_item_count,
              entry.fallback_item_count,
            )}
          />
          {entry.used_fallback ? (
            <Chip size="small" variant="outlined" label="Used fallback ordering" />
          ) : null}
          {rankedAt ? <Chip size="small" variant="outlined" label={`Ranked ${rankedAt}`} /> : null}
        </Stack>
        {entry.last_error ? (
          <Typography variant="caption" color="warning.main">
            Last error: {entry.last_error}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

const ScrollScorerStatusPanel: React.FC<{
  rankingStatus: ScrollScorerStatus;
}> = ({ rankingStatus }) => {
  const mode = SCORER_MODE_UI[rankingStatus.state];
  const rankingAlert = getRankingAlertConfig(rankingStatus);
  const scorerSummary = formatScorerSummary(rankingStatus);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Ranking status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scorerSummary ?? 'No refresh history yet.'}
            </Typography>
          </Box>
          <Chip size="small" color={mode.chipColor} label={mode.label} />
        </Box>

        {rankingAlert ? <Alert severity={rankingAlert.severity}>{rankingAlert.message}</Alert> : null}

        <Grid2 container spacing={1.5}>
          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                Latest refresh
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                {formatPublishedAt(rankingStatus.last_refresh_at) ?? 'Not refreshed yet'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {formatRefreshCounts(
                  rankingStatus.last_attempted_item_count,
                  rankingStatus.last_ranked_item_count,
                  rankingStatus.last_fallback_item_count,
                )}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                Last cloud success
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                {formatPublishedAt(rankingStatus.last_ranked_at) ?? 'No successful cloud rank yet'}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                Current mode
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                {mode.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {rankingStatus.detail}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                Latest error
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                {rankingStatus.last_error ?? 'No recent scorer error'}
              </Typography>
              {rankingStatus.last_error_at ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {formatPublishedAt(rankingStatus.last_error_at)}
                </Typography>
              ) : null}
            </Box>
          </Grid2>
        </Grid2>

        {rankingStatus.recent_refreshes.length > 0 ? (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Recent refreshes</Typography>
            <Stack spacing={1}>
              {rankingStatus.recent_refreshes.map((entry, index) => (
                <ScorerHistoryEntry key={`${entry.refreshed_at}-${index}`} entry={entry} />
              ))}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

const ScrollFeedCard: React.FC<{
  item: ScrollFeedItem;
  onOpenVideo: (item: ScrollFeedItem) => void;
}> = ({ item, onOpenVideo }) => (
  <Card>
    <CardContent>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Chip label={item.source === 'x' ? 'X / Twitter' : 'Instagram'} size="small" />
          {item.decision ? <Chip label={item.decision} size="small" variant="outlined" /> : null}
          {typeof item.interest_score === 'number' ? (
            <Chip label={`${Math.round(item.interest_score * 100)}% match`} size="small" />
          ) : null}
        </Stack>

        <Box>
          {item.author_name ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.author_name}
            </Typography>
          ) : null}
          {item.published_at ? (
            <Typography variant="body2" color="text.secondary">
              {formatPublishedAt(item.published_at)}
            </Typography>
          ) : null}
        </Box>

        {item.caption ? <Typography variant="body1">{item.caption}</Typography> : null}

        {item.video ? (
          <Box>
            {getVideoPosterUrl(item) ? (
              <Box
                component="img"
                src={getVideoPosterUrl(item) ?? undefined}
                alt={item.caption || item.author_name || 'Scroll video preview'}
                sx={{
                  width: '100%',
                  maxHeight: 420,
                  borderRadius: 2,
                  display: 'block',
                  objectFit: 'contain',
                  bgcolor: 'black',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: 240,
                  borderRadius: 2,
                  bgcolor: 'black',
                  color: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2">Open the live preview to watch this video.</Typography>
              </Box>
            )}
            <Alert severity="info" sx={{ mt: 1 }}>
              Browser-backed preview keeps playback inside the authenticated source page. Audio/native video controls come later.
            </Alert>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayIcon />}
              sx={{ mt: 1 }}
              onClick={() => onOpenVideo(item)}
            >
              Open live preview
            </Button>
          </Box>
        ) : item.preview_image_url ? (
          <Box
            component="img"
            src={item.preview_image_url}
            alt={item.caption || item.author_name || 'Scroll preview'}
            sx={{
              width: '100%',
              maxHeight: 420,
              borderRadius: 2,
              display: 'block',
              objectFit: 'contain',
              bgcolor: 'action.hover',
            }}
          />
        ) : null}

        {item.reason ? (
          <Typography variant="body2" color="text.secondary">
            {item.reason}
          </Typography>
        ) : null}

        {item.source_url ? (
          <Box>
            <Button
              component="a"
              href={item.source_url}
              target="_blank"
              rel="noreferrer"
              variant="text"
              size="small"
            >
              Open at source
            </Button>
          </Box>
        ) : null}
      </Stack>
    </CardContent>
  </Card>
);

const ScrollPage: React.FC = () => {
  const {
    config,
    sources,
    rankingStatus,
    items,
    loading,
    saving,
    refreshing,
    sourceBusy,
    videoSession,
    videoBusy,
    error,
    fetchState,
    saveConfig,
    refreshFeed,
    saveSourceSession,
    saveLiveSourceSession,
    connectSourceRuntime,
    openBrowserSession,
    closeBrowserSession,
    browserNavigate,
    browserBack,
    browserForward,
    browserReload,
    browserClick,
    browserType,
    browserPress,
    browserScroll,
    browserDrag,
    openVideoSession,
    refreshVideoSession,
    closeVideoSession,
    videoSessionClick,
    videoSessionScroll,
    clearSourceSession,
  } = useScrollStore();
  const [promptDraft, setPromptDraft] = React.useState('');
  const [enabledDraft, setEnabledDraft] = React.useState<Record<ScrollSourceId, boolean>>({
    x: true,
    instagram: true,
  });
  const [sessionDrafts, setSessionDrafts] = React.useState<Record<ScrollSourceId, SessionDraft>>({
    x: { ...EMPTY_SESSION_DRAFT },
    instagram: { ...EMPTY_SESSION_DRAFT },
  });
  const [activeVideoItem, setActiveVideoItem] = React.useState<ScrollFeedItem | null>(null);
  const browserPointerRef = React.useRef<{
    source: ScrollSourceId;
    clientX: number;
    clientY: number;
  } | null>(null);
  const suppressNextBrowserClickRef = React.useRef<ScrollSourceId | null>(null);
  const nativeVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const activeNativeCaptureRef = React.useRef<{
    sessionId: string;
    captureUrl: string;
    timelineStartSeconds: number | null;
  } | null>(null);
  const pendingNativeRestoreRef = React.useRef<{
    targetTime: number | null;
    shouldPlay: boolean;
  } | null>(null);

  React.useEffect(() => {
    void fetchState();
  }, [fetchState]);

  React.useEffect(() => {
    if (!config) {
      return;
    }
    setPromptDraft(config.prompt_profile.prompt);
    setEnabledDraft({
      x: config.sources.x?.enabled ?? true,
      instagram: config.sources.instagram?.enabled ?? true,
    });
    setSessionDrafts((current) => ({
      x: {
        ...current.x,
        loginHint: sources.find((item) => item.source === 'x')?.session_material.login_hint ?? current.x.loginHint,
        userAgent: sources.find((item) => item.source === 'x')?.session_material.user_agent ?? current.x.userAgent,
        browserUrl: sources.find((item) => item.source === 'x')?.current_url ?? current.x.browserUrl,
      },
      instagram: {
        ...current.instagram,
        loginHint:
          sources.find((item) => item.source === 'instagram')?.session_material.login_hint ??
          current.instagram.loginHint,
        userAgent:
          sources.find((item) => item.source === 'instagram')?.session_material.user_agent ??
          current.instagram.userAgent,
        browserUrl:
          sources.find((item) => item.source === 'instagram')?.current_url ?? current.instagram.browserUrl,
      },
    }));
  }, [config, sources]);

  React.useEffect(() => {
    if (!videoSession) {
      return;
    }
    if (videoSession.capture.status === 'failed') {
      return;
    }
    const intervalMs =
      videoSession.capture.status === 'ready' && !videoSession.capture.renewing ? 5000 : 1200;
    const timer = window.setInterval(() => {
      void refreshVideoSession(videoSession.session_id);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [refreshVideoSession, videoSession]);

  React.useEffect(() => {
    const video = nativeVideoRef.current;
    if (!video || !videoSession?.capture.capture_url) {
      activeNativeCaptureRef.current = null;
      pendingNativeRestoreRef.current = null;
      return;
    }
    const nextUrl = videoSession.capture.capture_url;
    const nextTimelineStart = videoSession.capture.timeline_start_seconds ?? 0;
    const activeCapture = activeNativeCaptureRef.current;
    if (
      activeCapture &&
      activeCapture.sessionId === videoSession.session_id &&
      activeCapture.captureUrl === nextUrl
    ) {
      return;
    }
    const targetTime =
      activeCapture && activeCapture.sessionId === videoSession.session_id
        ? Math.max(
            (activeCapture.timelineStartSeconds ?? 0) + (Number.isFinite(video.currentTime) ? video.currentTime : 0) - nextTimelineStart,
            0,
          )
        : null;
    pendingNativeRestoreRef.current = {
      targetTime,
      shouldPlay:
        activeCapture && activeCapture.sessionId === videoSession.session_id
          ? !video.paused && !video.ended
          : true,
    };
    activeNativeCaptureRef.current = {
      sessionId: videoSession.session_id,
      captureUrl: nextUrl,
      timelineStartSeconds: videoSession.capture.timeline_start_seconds,
    };
    if (video.getAttribute('src') !== nextUrl) {
      video.src = nextUrl;
      video.load();
    }
  }, [
    videoSession?.capture.capture_url,
    videoSession?.capture.timeline_start_seconds,
    videoSession?.session_id,
  ]);

  const handleNativeVideoLoadedMetadata = React.useCallback(() => {
    const video = nativeVideoRef.current;
    const pendingRestore = pendingNativeRestoreRef.current;
    if (!video || !pendingRestore) {
      return;
    }
    pendingNativeRestoreRef.current = null;
    if (pendingRestore.targetTime !== null && Number.isFinite(pendingRestore.targetTime)) {
      const boundedTime =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.min(Math.max(pendingRestore.targetTime, 0), Math.max(video.duration - 0.1, 0))
          : Math.max(pendingRestore.targetTime, 0);
      if (boundedTime > 0) {
        video.currentTime = boundedTime;
      }
    }
    if (pendingRestore.shouldPlay) {
      void video.play().catch(() => undefined);
    }
  }, []);

  const hasConfigChanges =
    config !== null &&
    (promptDraft !== config.prompt_profile.prompt ||
      enabledDraft.x !== (config.sources.x?.enabled ?? true) ||
      enabledDraft.instagram !== (config.sources.instagram?.enabled ?? true));
  const hasConnectedSource = sources.some((item) => item.session_status === 'connected');

  const handleSave = async () => {
    await saveConfig({
      prompt_profile: { prompt: promptDraft },
      sources: {
        x: { enabled: enabledDraft.x },
        instagram: { enabled: enabledDraft.instagram },
      },
    });
  };

  const handleSessionFieldChange = (
    source: ScrollSourceId,
    field: keyof SessionDraft,
    value: string | null,
  ) => {
    setSessionDrafts((current) => ({
      ...current,
      [source]: {
        ...current[source],
        [field]: value ?? '',
        error: field === 'storageState' ? null : current[source].error,
      },
    }));
  };

  const handleSaveSession = async (source: ScrollSourceId) => {
    const draft = sessionDrafts[source];
    let parsed: { cookies?: Record<string, unknown>[]; origins?: Record<string, unknown>[] };
    try {
      parsed = draft.storageState.trim() ? JSON.parse(draft.storageState) : {};
    } catch {
      setSessionDrafts((current) => ({
        ...current,
        [source]: {
          ...current[source],
          error: 'Storage state must be valid JSON exported from Playwright.',
        },
      }));
      return;
    }

    await saveSourceSession(source, {
      login_hint: draft.loginHint || undefined,
      user_agent: draft.userAgent || undefined,
      storage_state: {
        cookies: Array.isArray(parsed.cookies) ? parsed.cookies : [],
        origins: Array.isArray(parsed.origins) ? parsed.origins : [],
      },
    });

    setSessionDrafts((current) => ({
      ...current,
      [source]: {
        ...current[source],
        storageState: '',
        error: null,
      },
    }));
  };

  const handleBrowserNavigate = async (source: ScrollSourceId, browserStream: ScrollBrowserStreamSession) => {
    const nextUrl = sessionDrafts[source].browserUrl.trim();
    if (!nextUrl) {
      return;
    }
    await browserNavigate(source, browserStream.session_id, nextUrl);
  };

  const handleBrowserType = async (source: ScrollSourceId, browserStream: ScrollBrowserStreamSession) => {
    const text = sessionDrafts[source].browserText;
    if (!text.trim()) {
      return;
    }
    await browserType(source, browserStream.session_id, text);
    setSessionDrafts((current) => ({
      ...current,
      [source]: {
        ...current[source],
        browserText: '',
      },
    }));
  };

  const handleBrowserReplace = async (source: ScrollSourceId, browserStream: ScrollBrowserStreamSession) => {
    const text = sessionDrafts[source].browserText;
    if (!text.trim()) {
      return;
    }
    await browserType(source, browserStream.session_id, text, true);
    setSessionDrafts((current) => ({
      ...current,
      [source]: {
        ...current[source],
        browserText: '',
      },
    }));
  };

  const handleBrowserClick = async (
    source: ScrollSourceId,
    browserStream: ScrollBrowserStreamSession,
    event: React.MouseEvent<HTMLImageElement>,
  ) => {
    if (suppressNextBrowserClickRef.current === source) {
      suppressNextBrowserClickRef.current = null;
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.max(
      0,
      Math.min(browserStream.viewport_width, ((event.clientX - rect.left) / rect.width) * browserStream.viewport_width),
    );
    const y = Math.max(
      0,
      Math.min(
        browserStream.viewport_height,
        ((event.clientY - rect.top) / rect.height) * browserStream.viewport_height,
      ),
    );
    await browserClick(source, browserStream.session_id, x, y);
  };

  const handleBrowserPointerDown = (source: ScrollSourceId, event: React.MouseEvent<HTMLImageElement>) => {
    browserPointerRef.current = {
      source,
      clientX: event.clientX,
      clientY: event.clientY,
    };
  };

  const handleBrowserPointerUp = async (
    source: ScrollSourceId,
    browserStream: ScrollBrowserStreamSession,
    event: React.MouseEvent<HTMLImageElement>,
  ) => {
    const start = browserPointerRef.current;
    browserPointerRef.current = null;
    if (!start || start.source !== source) {
      return;
    }
    const dragDistance = Math.hypot(event.clientX - start.clientX, event.clientY - start.clientY);
    if (dragDistance < 8) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const projectX = (clientX: number) =>
      Math.max(0, Math.min(browserStream.viewport_width, ((clientX - rect.left) / rect.width) * browserStream.viewport_width));
    const projectY = (clientY: number) =>
      Math.max(
        0,
        Math.min(browserStream.viewport_height, ((clientY - rect.top) / rect.height) * browserStream.viewport_height),
      );
    suppressNextBrowserClickRef.current = source;
    await browserDrag(
      source,
      browserStream.session_id,
      projectX(start.clientX),
      projectY(start.clientY),
      projectX(event.clientX),
      projectY(event.clientY),
    );
  };

  const handleOpenVideoPreview = async (item: ScrollFeedItem) => {
    setActiveVideoItem(item);
    await openVideoSession(item.id);
  };

  const handleCloseVideoPreview = async () => {
    await closeVideoSession();
    setActiveVideoItem(null);
  };

  const handleVideoPreviewClick = async (
    session: ScrollVideoBrowserSession,
    event: React.MouseEvent<HTMLImageElement>,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(session.viewport_width, ((event.clientX - rect.left) / rect.width) * session.viewport_width));
    const y = Math.max(
      0,
      Math.min(session.viewport_height, ((event.clientY - rect.top) / rect.height) * session.viewport_height),
    );
    await videoSessionClick(session.session_id, x, y);
  };

  return (
    <PageLayout contentMaxWidth={1600} paddingMode="shell">
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            The Scroll
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Live-session-backed intake for X and Instagram, ranked into AH-controlled cards with
            prompt-tuned filtering and room for inline video previews.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Grid2 container spacing={{ xs: 2, md: 3 }}>
          <Grid2 size={{ xs: 12, lg: 5 }}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PromptIcon fontSize="small" />
                    <Typography variant="h6">Interest prompt</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    This shared profile guides ranking across every connected source.
                  </Typography>
                  <TextField
                    label="Scroll prompt"
                    multiline
                    minRows={8}
                    value={promptDraft}
                    onChange={(event) => setPromptDraft(event.target.value)}
                    placeholder="Describe the kinds of posts, visuals, topics, and tone you want The Scroll to prioritize."
                    fullWidth
                  />
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={() => void handleSave()}
                      disabled={!hasConfigChanges || saving || loading}
                    >
                      {saving ? 'Saving…' : 'Save Scroll profile'}
                    </Button>
                  </Box>
                  {config?.prompt_profile.updated_at ? (
                    <Typography variant="caption" color="text.secondary">
                      Updated {formatPublishedAt(config.prompt_profile.updated_at)}
                    </Typography>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, lg: 7 }}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FeedIcon fontSize="small" />
                    <Typography variant="h6">Source sessions</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    These sources stay live behind the scenes so collection can act more like an
                    ongoing browsing session than a bursty scrape.
                  </Typography>

                  <Grid2 container spacing={2}>
                    {sourceOrder.map((sourceId) => {
                      const sourceState = sources.find((item) => item.source === sourceId);
                      const enabled = enabledDraft[sourceId];
                      const sessionDraft = sessionDrafts[sourceId];
                      const sessionSummary = sourceState?.session_material;
                      const isBusy = sourceBusy[sourceId];

                      return (
                        <Grid2 key={sourceId} size={{ xs: 12, md: 6 }}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                              <Stack spacing={2}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                  }}
                                >
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {sourceState?.label ?? (sourceId === 'x' ? 'X / Twitter' : 'Instagram')}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    color={
                                      sourceState?.session_status === 'connected'
                                        ? 'success'
                                        : sourceState?.session_status === 'disabled'
                                          ? 'default'
                                          : 'warning'
                                    }
                                    label={STATUS_LABELS[sourceState?.session_status ?? 'not_connected']}
                                  />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {sourceState?.status_detail ??
                                    'Session status will appear here once the live collector is available.'}
                                </Typography>
                                {sourceState?.runtime_started_at ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Live runtime started {formatPublishedAt(sourceState.runtime_started_at)}
                                  </Typography>
                                ) : null}
                                {sourceState?.last_collected_at ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Last collected {formatPublishedAt(sourceState.last_collected_at)}
                                  </Typography>
                                ) : null}
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={enabled}
                                      onChange={(event) =>
                                        setEnabledDraft((current) => ({
                                          ...current,
                                          [sourceId]: event.target.checked,
                                        }))
                                      }
                                    />
                                  }
                                  label={enabled ? 'Enabled in Scroll' : 'Disabled in Scroll'}
                                />

                                <Stack spacing={1.5}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <LockIcon fontSize="small" />
                                    <Typography variant="subtitle2">Encrypted session</Typography>
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    {sessionSummary?.persisted
                                      ? `Saved ${sessionSummary.cookie_count} cookies and ${sessionSummary.origin_count} origins${sessionSummary.login_hint ? ` for ${sessionSummary.login_hint}` : ''}.`
                                      : 'Open the live browser below to log in here, or paste Playwright storage-state JSON from another trusted browser session.'}
                                  </Typography>
                                  {sessionSummary?.updated_at ? (
                                    <Typography variant="caption" color="text.secondary">
                                      Updated {formatPublishedAt(sessionSummary.updated_at)}
                                    </Typography>
                                  ) : null}
                                  <TextField
                                    label="Account label (optional)"
                                    value={sessionDraft.loginHint}
                                    onChange={(event) =>
                                      handleSessionFieldChange(sourceId, 'loginHint', event.target.value)
                                    }
                                    fullWidth
                                  />
                                  <TextField
                                    label="User agent (optional)"
                                    value={sessionDraft.userAgent}
                                    onChange={(event) =>
                                      handleSessionFieldChange(sourceId, 'userAgent', event.target.value)
                                    }
                                    fullWidth
                                  />
                                  <TextField
                                    label="Playwright storage-state JSON"
                                    value={sessionDraft.storageState}
                                    onChange={(event) =>
                                      handleSessionFieldChange(sourceId, 'storageState', event.target.value)
                                    }
                                    placeholder='{"cookies":[...],"origins":[...]}'
                                    multiline
                                    minRows={6}
                                    fullWidth
                                    error={Boolean(sessionDraft.error)}
                                    helperText={
                                      sessionDraft.error ??
                                      'The backend encrypts this payload before writing it to Drive.'
                                    }
                                  />
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                      <Button
                                        variant="contained"
                                      size="small"
                                      startIcon={<SaveIcon />}
                                      onClick={() => void handleSaveSession(sourceId)}
                                      disabled={isBusy || !sessionDraft.storageState.trim()}
                                    >
                                      {isBusy ? 'Saving…' : 'Save pasted session'}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<SaveIcon />}
                                      onClick={() => void saveLiveSourceSession(sourceId, sessionDraft.loginHint || undefined)}
                                      disabled={isBusy || sourceState?.session_status !== 'connected'}
                                    >
                                      Save live session
                                    </Button>
                                    {sessionSummary?.persisted ? (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<RestartIcon />}
                                        onClick={() => void connectSourceRuntime(sourceId)}
                                        disabled={isBusy || !enabled}
                                      >
                                        {sourceState?.session_status === 'connected'
                                          ? 'Restart runtime'
                                          : 'Connect runtime'}
                                      </Button>
                                    ) : null}
                                    {sessionSummary?.persisted ? (
                                      <Button
                                        variant="text"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => void clearSourceSession(sourceId)}
                                        disabled={isBusy}
                                      >
                                        Clear session
                                      </Button>
                                    ) : null}
                                  </Stack>
                                </Stack>

                                <Stack
                                  spacing={1.5}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    p: 1.5,
                                  }}
                                >
                                  <Typography variant="subtitle2">Live browser stream</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Use the backend-owned browser to log in without exposing cookies or
                                    source DOM to the frontend. Click inside the stream to focus, or drag
                                    directly on it for slider and swipe-style gestures.
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => void openBrowserSession(sourceId)}
                                      disabled={isBusy || !enabled}
                                    >
                                      {sourceState?.browser_stream ? 'Refresh live view' : 'Open live browser'}
                                    </Button>
                                    {sourceState?.browser_stream ? (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => void closeBrowserSession(sourceId)}
                                        disabled={isBusy}
                                      >
                                        Close live view
                                      </Button>
                                    ) : null}
                                  </Stack>

                                   {sourceState?.browser_stream ? (
                                     <Stack spacing={1.25}>
                                       {sourceState.browser_stream.page_title ? (
                                         <Typography variant="caption" color="text.secondary">
                                           {sourceState.browser_stream.page_title}
                                         </Typography>
                                       ) : null}
                                       <Typography variant="caption" color="text.secondary">
                                         Stream lease ends {formatPublishedAt(sourceState.browser_stream.expires_at)}
                                       </Typography>
                                       <TextField
                                         label="Browser URL"
                                        value={sessionDraft.browserUrl}
                                        onChange={(event) =>
                                          handleSessionFieldChange(sourceId, 'browserUrl', event.target.value)
                                         }
                                         fullWidth
                                       />
                                       <Stack spacing={0.75}>
                                         <Typography variant="caption" color="text.secondary">
                                           Navigation
                                         </Typography>
                                         <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserBack(sourceId, sourceState.browser_stream!.session_id)
                                             }
                                             disabled={isBusy}
                                           >
                                             Back
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserForward(sourceId, sourceState.browser_stream!.session_id)
                                             }
                                             disabled={isBusy}
                                           >
                                             Forward
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserReload(sourceId, sourceState.browser_stream!.session_id)
                                             }
                                             disabled={isBusy}
                                           >
                                             Reload
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserNavigate(
                                                 sourceId,
                                                 sourceState.browser_stream!.session_id,
                                                 SOURCE_HOME_URLS[sourceId],
                                               )
                                             }
                                             disabled={isBusy}
                                           >
                                             Home
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void handleBrowserNavigate(sourceId, sourceState.browser_stream!)
                                             }
                                             disabled={isBusy || !sessionDraft.browserUrl.trim()}
                                           >
                                             Navigate
                                           </Button>
                                         </Stack>
                                       </Stack>
                                       <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                         <Button
                                           variant="outlined"
                                           size="small"
                                           onClick={() =>
                                             void browserScroll(sourceId, sourceState.browser_stream!.session_id, 0, -640)
                                          }
                                          disabled={isBusy}
                                        >
                                          Scroll up
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={() =>
                                            void browserScroll(sourceId, sourceState.browser_stream!.session_id, 0, 640)
                                          }
                                          disabled={isBusy}
                                         >
                                           Scroll down
                                         </Button>
                                       </Stack>
                                       <Box
                                         sx={{
                                           borderRadius: 2,
                                          overflow: 'hidden',
                                          bgcolor: 'black',
                                          border: '1px solid',
                                          borderColor: 'divider',
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={sourceState.browser_stream.stream_url}
                                          alt={`${sourceState.label} live browser`}
                                          draggable={false}
                                          onMouseDown={(event: React.MouseEvent<HTMLImageElement>) =>
                                            handleBrowserPointerDown(sourceId, event)
                                          }
                                          onMouseUp={(event: React.MouseEvent<HTMLImageElement>) =>
                                            void handleBrowserPointerUp(sourceId, sourceState.browser_stream!, event)
                                          }
                                          onClick={(event: React.MouseEvent<HTMLImageElement>) =>
                                            void handleBrowserClick(sourceId, sourceState.browser_stream!, event)
                                          }
                                          sx={{
                                            display: 'block',
                                            width: '100%',
                                            maxHeight: 420,
                                            objectFit: 'contain',
                                            cursor: 'crosshair',
                                          }}
                                        />
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        {sourceState.browser_stream.current_url ??
                                          sourceState.current_url ??
                                          'Click in the stream to focus the next field.'}
                                      </Typography>
                                       <Typography variant="caption" color="text.secondary">
                                         Drag inside the stream for swipe, slider, or scrub-style interactions.
                                       </Typography>
                                       <Stack spacing={0.75}>
                                         <Typography variant="caption" color="text.secondary">
                                           Focused field helpers
                                         </Typography>
                                         <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserPress(
                                                 sourceId,
                                                 sourceState.browser_stream!.session_id,
                                                 'Shift+Tab',
                                               )
                                             }
                                             disabled={isBusy}
                                           >
                                             Previous field
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserPress(sourceId, sourceState.browser_stream!.session_id, 'Tab')
                                             }
                                             disabled={isBusy}
                                           >
                                             Next field
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserPress(
                                                 sourceId,
                                                 sourceState.browser_stream!.session_id,
                                                 'Enter',
                                               )
                                             }
                                             disabled={isBusy}
                                           >
                                             Submit
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserPress(
                                                 sourceId,
                                                 sourceState.browser_stream!.session_id,
                                                 'Escape',
                                               )
                                             }
                                             disabled={isBusy}
                                           >
                                             Dismiss
                                           </Button>
                                           <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() =>
                                               void browserPress(
                                                 sourceId,
                                                 sourceState.browser_stream!.session_id,
                                                 'Backspace',
                                               )
                                             }
                                             disabled={isBusy}
                                           >
                                             Backspace
                                           </Button>
                                         </Stack>
                                       </Stack>
                                       <TextField
                                         label="Type into focused field"
                                         value={sessionDraft.browserText}
                                        onChange={(event) =>
                                          handleSessionFieldChange(sourceId, 'browserText', event.target.value)
                                        }
                                        fullWidth
                                      />
                                      <Box>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => void handleBrowserType(sourceId, sourceState.browser_stream!)}
                                            disabled={isBusy || !sessionDraft.browserText.trim()}
                                          >
                                            Append text
                                          </Button>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() =>
                                              void handleBrowserReplace(sourceId, sourceState.browser_stream!)
                                            }
                                            disabled={isBusy || !sessionDraft.browserText.trim()}
                                          >
                                            Replace field
                                          </Button>
                                        </Stack>
                                      </Box>
                                    </Stack>
                                  ) : (
                                    <Alert severity="info">
                                      Open the live browser to sign in directly inside AH Home. Once the
                                      source is authenticated, save the live session to keep it across
                                      backend restarts.
                                    </Alert>
                                  )}
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </Stack>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

            <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box>
                  <Typography variant="h6">Unified feed</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Refresh collects from connected live sessions, then ranks the normalized cards
                    against your Scroll prompt.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<RestartIcon />}
                  onClick={() => void refreshFeed()}
                  disabled={refreshing || loading || !hasConnectedSource}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh feed'}
                </Button>
              </Box>
              {rankingStatus ? <ScrollScorerStatusPanel rankingStatus={rankingStatus} /> : null}
              {loading && items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Loading Scroll state…
                </Typography>
              ) : items.length === 0 ? (
                <Stack spacing={2}>
                  <Alert severity="info">
                    {hasConnectedSource
                      ? 'No Scroll items yet. Try refreshing after your live source sessions finish loading feed content.'
                      : 'No Scroll items yet. Save a source session, connect its live runtime, then refresh the feed.'}
                  </Alert>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {items.map((item) => (
                    <ScrollFeedCard key={item.id} item={item} onOpenVideo={handleOpenVideoPreview} />
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={Boolean(activeVideoItem?.video)}
        onClose={() => void handleCloseVideoPreview()}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {activeVideoItem?.author_name || activeVideoItem?.source || 'Video preview'}
        </DialogTitle>
        <DialogContent>
          {activeVideoItem?.video ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">
                Scroll now buffers playback inside the authenticated source page first, then serves the captured media from AH once enough native-context video is ready.
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayIcon />}
                  onClick={() => void openVideoSession(activeVideoItem.id)}
                  disabled={videoBusy}
                >
                  {videoBusy ? 'Opening…' : 'Restart preview'}
                </Button>
                {videoSession ? (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => void videoSessionScroll(videoSession.session_id, 0, -420)}
                      disabled={videoBusy}
                    >
                      Scroll up
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => void videoSessionScroll(videoSession.session_id, 0, 420)}
                      disabled={videoBusy}
                    >
                      Scroll down
                    </Button>
                  </>
                ) : null}
                {activeVideoItem.source_url ? (
                  <Button
                    component="a"
                    href={activeVideoItem.source_url}
                    target="_blank"
                    rel="noreferrer"
                    variant="text"
                    size="small"
                  >
                    Open at source
                  </Button>
                ) : null}
              </Stack>
              {videoSession && videoSession.item_id === activeVideoItem.id ? (
                <>
                  {videoSession.capture.capture_url ? (
                    <Box
                      component="video"
                      ref={nativeVideoRef}
                      controls
                      autoPlay
                      playsInline
                      onLoadedMetadata={handleNativeVideoLoadedMetadata}
                      poster={getVideoPosterUrl(activeVideoItem) ?? undefined}
                      sx={{
                        width: '100%',
                        maxHeight: '70vh',
                        display: 'block',
                        bgcolor: 'black',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: 'black',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box
                        component="img"
                        src={videoSession.stream_url}
                        alt={`${activeVideoItem.author_name || activeVideoItem.source} live preview`}
                        onClick={(event: React.MouseEvent<HTMLImageElement>) =>
                          void handleVideoPreviewClick(videoSession, event)
                        }
                        sx={{
                          width: '100%',
                          maxHeight: '70vh',
                          display: 'block',
                          objectFit: 'contain',
                          cursor: 'pointer',
                        }}
                      />
                    </Box>
                  )}
                  {videoSession.capture.status === 'ready' ? (
                    <Alert severity="success">
                      AH is now serving a buffered native-context capture from the authenticated source page.
                      Captured {formatBytes(videoSession.capture.bytes_captured)}
                      {videoSession.capture.ready_at
                        ? ` • ready ${formatPublishedAt(videoSession.capture.ready_at)}`
                        : ''}
                      {videoSession.capture.renewing
                        ? ` • refreshing the next segment in the background (${formatBytes(videoSession.capture.renewal_bytes_captured)} buffered so far).`
                        : '.'}
                    </Alert>
                  ) : videoSession.capture.status === 'failed' ? (
                    <Alert severity="warning">
                      Buffered capture could not be prepared, so the dialog stayed on the live browser preview.
                      {videoSession.capture.last_error ? ` ${videoSession.capture.last_error}` : ''}
                    </Alert>
                  ) : (
                    <Alert severity="info">
                      Buffering native playback inside the authenticated source page…
                      {' '}
                      {formatBytes(videoSession.capture.bytes_captured)} captured so far.
                    </Alert>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {videoSession.capture.status === 'ready'
                      ? 'AH keeps polling this session for newer buffered segments. If the source swaps videos or the segment ages out, the dialog will briefly fall back to the live preview while it renews.'
                      : 'Click inside the preview to play/pause or interact with the source page while AH finishes buffering the native capture.'}
                    {' '}
                    Lease ends {formatPublishedAt(videoSession.expires_at)}.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {videoSession.current_url ?? videoSession.source_url}
                  </Typography>
                </>
              ) : (
                <Box
                  sx={{
                    minHeight: 260,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {videoBusy ? 'Opening browser-backed preview…' : 'Preview unavailable.'}
                  </Typography>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default ScrollPage;
